import { SimpleDefinitionListItem } from "@components"
import { getDateRangeStatus, formattedDateRange, hasMeetingType, mySqlToLocal } from "@helpers"

import { 
  BookmarkSquareIcon, 
  CalendarDaysIcon, 
  Cog6ToothIcon, 
  DocumentIcon, 
  DocumentTextIcon, 
  IdentificationIcon, 
  MapPinIcon,
  Square3Stack3DIcon 
} from "@heroicons/react/24/outline"

import PropTypes from "prop-types"

const EventDetails = ({ event, lessons = [], session, location }) => {
  const eventDate = event.start_date === null ? "No Date Set" : formattedDateRange(event.start_date, event.end_date, true, "D", "MMM", "YYYY")
  const status = getDateRangeStatus(event.start_date, event.end_date)

  return (
    <div>
      <div id="event-details" className="mb-8">
        <h2 className="mb-1 text-lg font-semibold text-content2-foreground">Event Details</h2>
        <SimpleDefinitionListItem caption="Event Date" description={eventDate} icon={<CalendarDaysIcon />} />
        <SimpleDefinitionListItem caption="Event Status" icon={<BookmarkSquareIcon />} description={status} />        
        <SimpleDefinitionListItem caption="Event Id" description={event.id} icon={<IdentificationIcon />} />
        <SimpleDefinitionListItem caption="Title" description={event.title} icon={<DocumentIcon />} />
        <SimpleDefinitionListItem caption="Description" description={event.description} icon={<DocumentTextIcon />} /> {/** first x chars dd */}
        <SimpleDefinitionListItem caption="No of Sessions" description={event.sessions.length} icon={<Square3Stack3DIcon />} showBorder={false} />
      </div>

      <div id="event-settings" className="mb-8">
        <h2 className="mt-4 mb-1 text-lg font-semibold text-content2-foreground">Event Settings</h2>

        <SimpleDefinitionListItem caption="Max Booking per Student" icon={<Cog6ToothIcon />}>
          <p className="font-semibold text-content2-foreground">
            {event.max_bookings_per_student === 0 ? "Unlimited" : event.max_bookings_per_student}
          </p>
        </SimpleDefinitionListItem>
        
        
        <SimpleDefinitionListItem caption="Open to Parents" icon={<Cog6ToothIcon />}>
          <p className="font-semibold text-content2-foreground">{event.open_parents ? "Yes" : "No"}</p>
        </SimpleDefinitionListItem>

        {event.meetingTypes.length > 0 && hasMeetingType("VC", event.meetingTypes) && (
          <SimpleDefinitionListItem caption="VC Auto Disconnect" icon={<Cog6ToothIcon />}>
            <p className="font-semibold text-content2-foreground">{event.vc_auto_disconnect ? "Enabled" : "Disabled"}</p>
          </SimpleDefinitionListItem>
        )}

        <SimpleDefinitionListItem caption="Meeting Types" icon={<Cog6ToothIcon />} description={event.meetingTypes.map(type => type.name).join(", ")} showBorder={false} />
      </div>

      <div id="current-session" className="mb-8">
        <h2 className="mt-4 mb-1 text-lg font-semibold text-content2-foreground">Current Session</h2>
        <SimpleDefinitionListItem caption="Name" description={session.title} icon={<Square3Stack3DIcon />} />
        <SimpleDefinitionListItem caption="Location" description={location?.name || "N/A"} icon={<MapPinIcon />} />
        <SimpleDefinitionListItem caption="Slot Gap" description={`${session.slot_gap} mins`} icon={<Square3Stack3DIcon />} />
        <SimpleDefinitionListItem caption="Slot Duration" description={`${session.slot_duration} mins` } icon={<Square3Stack3DIcon />} />
        <SimpleDefinitionListItem caption="Session Starts" description={mySqlToLocal(session.starts_at).format("DD MMM HH:mm")} icon={<Square3Stack3DIcon />} />
        <SimpleDefinitionListItem caption="Session Ends" description={mySqlToLocal(session.ends_at).format("DD MMM HH:mm")} icon={<Square3Stack3DIcon />} showBorder={false} />
      </div>
      
      {lessons && lessons.length > 0 && (
        <div id="session-lessons" className="mb-8">
          <h2 className="mt-4 mb-1 text-lg font-semibold text-content2-foreground">Current Session Lessons</h2>
          
          {lessons.map((lesson, index) => (
            <SimpleDefinitionListItem 
              key={lesson.id} 
              caption="Lesson" 
              description={lesson.name} 
              icon={<BookmarkSquareIcon />}
              showBorder={index !== lessons.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

EventDetails.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    sessions: PropTypes.arrayOf(PropTypes.object).isRequired,
    max_bookings_per_student: PropTypes.number.isRequired,
    open_parents: PropTypes.bool.isRequired,
    meetingTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
    vc_auto_disconnect: PropTypes.bool.isRequired,
  }).isRequired,
  
  lessons: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),

  location: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),

  session: PropTypes.shape({
    title: PropTypes.string.isRequired,
    slot_gap: PropTypes.number.isRequired,
    slot_duration: PropTypes.number.isRequired,
    starts_at: PropTypes.string.isRequired,
    ends_at: PropTypes.string.isRequired,
  }).isRequired,

}

export default EventDetails
